import { FiscalYearMenu } from './FiscalYearMenu'
import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { CodeLookup } from '@/features/lookup/CodeLookup'
import { UserMenu } from './UserMenu'

/**
 * App shell — persistent chrome for every authenticated screen.
 *
 * §7.5 requires the "Look up code" affordance to be reachable from every
 * screen, so it lives here rather than on individual pages. It's wired up in
 * Step 8; the button is present and disabled until then so the layout is
 * settled and nothing shifts later.
 */

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/receipts', label: 'Receipts', end: false },
  { to: '/transactions', label: 'Transactions', end: false },
  { to: '/external', label: 'External charges', end: false },
  { to: '/import', label: 'Import', end: false },
  { to: '/reconcile', label: 'Reconcile', end: false },
] as const

export function AppShell() {
  const [lookupOpen, setLookupOpen] = useState(false)

  // Cmd/Ctrl-K from anywhere. §7.5 wants lookup reachable from every screen;
  // on desktop that means not having to aim at a button first.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setLookupOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className="min-h-dvh">
      {/* The installed PWA draws under the status bar — `viewport-fit=cover`
          in index.html is what puts it there — so the title landed on top of
          the clock. The inset is zero in a browser tab and on desktop, which
          is why this only ever showed up on the phone. */}
      <header
        className="border-b border-slate-200 bg-white"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-6">
          <NavLink to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt=""
              // Decorative — the wordmark beside it already names the app, so
              // a screen reader announcing both would just say it twice.
              className="size-7 shrink-0 sm:size-8"
            />
            {/* `sup` rather than a bare ™ so it sits raised and small, and
                `aria-hidden` because a screen reader announcing "trade mark"
                on every page is noise. Same treatment as the sign-in screen. */}
            <span className="text-lg font-semibold tracking-tight">
              Budget Monkey
              <sup aria-hidden="true" className="ml-0.5 align-super text-[0.6em] font-normal">
                ™
              </sup>
            </span>
          </NavLink>

          <FiscalYearMenu />

          <div className="ml-auto flex items-center gap-3">
            <button
              type="button"
              onClick={() => setLookupOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
            >
              Look up code
              <kbd className="hidden rounded border border-slate-300 px-1.5 text-xs font-normal text-slate-400 sm:inline">
                ⌘K
              </kbd>
            </button>
            <UserMenu />
          </div>
        </div>

        <nav aria-label="Main" className="mx-auto max-w-5xl px-4 sm:px-6">
          {/* Wraps rather than scrolling sideways. `overflow-x-auto` hid the
              last item completely on any window narrower than the full nav:
              macOS renders no scrollbar until you actually scroll, so there
              was nothing on screen to suggest more tabs existed. Two short
              rows on a narrow window beats a tab nobody can find. */}
          <ul className="flex flex-wrap gap-x-1">
            {NAV.map(({ to, label, end }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    [
                      'inline-block whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition',
                      isActive
                        ? 'border-slate-900 text-slate-900'
                        : 'border-transparent text-slate-500 hover:text-slate-900',
                    ].join(' ')
                  }
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <Outlet />
      </main>

      {/* The "Signed in as …" footer is gone: the avatar menu names the
          account, and repeating it at the bottom of every screen was the only
          reason it was there. */}

      <CodeLookup open={lookupOpen} onClose={() => setLookupOpen(false)} />
    </div>
  )
}
